import type { OAuthTransactionStorage } from '@palbase/auth';
import type { CookieStoreLike } from './server.js';

const attributes = { httpOnly: true, secure: true, sameSite: 'lax' as const, path: '/' };

function encode(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes))
    .replaceAll('+', '-')
    .replaceAll('/', '_')
    .replaceAll('=', '');
}

async function cookieName(key: string): Promise<string> {
  const hash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(key));
  return `__Host-palbe.oauth.${encode(new Uint8Array(hash))}`;
}

/**
 * The backend authenticates this random proof against its S256 challenge.
 * Host-only HttpOnly cookies keep it out of JavaScript and callback URLs.
 * A separate cookie for each scoped transaction supports concurrent tabs.
 */
export function oauthCookieStorage(store: CookieStoreLike): OAuthTransactionStorage {
  const set = (name: string, value: string, maxAge: number) => {
    if (!store.set)
      throw new Error(
        'Start or complete OAuth in a Server Action or Route Handler with writable cookies',
      );
    store.set(name, value, { ...attributes, maxAge });
  };
  return {
    async load(key) {
      const value = store.get(await cookieName(key))?.value;
      if (value === undefined) return null;
      if (value.length > 3800 || !/^[A-Za-z0-9_-]+$/.test(value))
        throw new Error('Invalid OAuth proof cookie');
      const bytes = Uint8Array.from(atob(value.replaceAll('-', '+').replaceAll('_', '/')), (c) =>
        c.charCodeAt(0),
      );
      return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
    },
    async save(key, value, expiresAt) {
      const encoded = encode(new TextEncoder().encode(value));
      if (encoded.length > 3800)
        throw new Error('OAuth transaction exceeds the proof cookie size limit');
      const ttl = Math.ceil((Date.parse(expiresAt) - Date.now()) / 1000);
      if (!(ttl > 0 && ttl <= 600)) throw new Error('OAuth transaction has an invalid expiry');
      set(await cookieName(key), encoded, ttl);
    },
    async remove(key) {
      set(await cookieName(key), '', 0);
    },
  };
}
